import { Sparkles, ArrowRight, Zap } from "lucide-react";
import { Button } from "@asm/ui";
import type { UpgradeHint as UpgradeHintType } from "@/lib/api";

interface UpgradeHintProps {
  hint: UpgradeHintType;
  variant?: "inline" | "card";
  className?: string;
}

const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  starter: {
    bg: "bg-cyan-500/10",
    text: "text-cyan-400",
    border: "border-cyan-500/30",
  },
  professional: {
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    border: "border-amber-500/30",
  },
  enterprise: {
    bg: "bg-purple-500/10",
    text: "text-purple-400",
    border: "border-purple-500/30",
  },
};

export function UpgradeHint({
  hint,
  variant = "inline",
  className = "",
}: UpgradeHintProps) {
  const tierKey = hint.required_tier.toLowerCase();
  const colors = TIER_COLORS[tierKey] || TIER_COLORS.starter;

  if (variant === "card") {
    return (
      <div
        className={`
          relative overflow-hidden rounded-lg p-4
          border ${colors.border} ${colors.bg}
          ${className}
        `}
      >
        {/* Decorative gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

        {/* Animated sparkle effect */}
        <div className="absolute top-2 right-2 opacity-50">
          <Sparkles className={`h-5 w-5 ${colors.text} animate-pulse`} />
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`h-4 w-4 ${colors.text}`} />
            <span className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>
              Unlock More
            </span>
          </div>

          <h4 className="font-semibold text-foreground mb-1">
            {hint.feature}
          </h4>

          <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
            {hint.description}
          </p>

          <div className="flex items-center justify-between">
            <span
              className={`
                px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider
                ${colors.bg} ${colors.text} border ${colors.border}
              `}
            >
              {hint.required_tier}
            </span>

            <Button
              variant="ghost"
              size="sm"
              className={`gap-1 ${colors.text} hover:${colors.bg}`}
            >
              Upgrade
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant
  return (
    <div
      className={`
        inline-flex items-center gap-3 px-3 py-2 rounded-lg
        border ${colors.border} ${colors.bg}
        ${className}
      `}
    >
      <Sparkles className={`h-4 w-4 ${colors.text} flex-shrink-0`} />
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">
          {hint.feature} requires
        </span>
        <span
          className={`
            px-1.5 py-0.5 rounded text-xs font-semibold uppercase tracking-wider
            ${colors.bg} ${colors.text} border ${colors.border}
          `}
        >
          {hint.required_tier}
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className={`ml-auto gap-1 ${colors.text} hover:${colors.bg} h-7 px-2`}
      >
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default UpgradeHint;
